import { defineStore } from 'pinia';
import { createAuthModule } from '@/store/modules/auth';
import { createMainDataModule } from '@/store/modules/main-data';
import { createBillingModule } from '@/store/modules/billing';
import { createDashboardModule } from '@/store/modules/dashboard';
import { createReportColumnsModule } from '@/store/modules/report-columns';
import { createCacheModule } from '@/store/modules/cache';
import { createLayoutUiModule } from '@/store/modules/layout-ui';
import { createTableModule } from '@/store/modules/table';
import { setTableSelectedReport } from '@/store/modules/table/accessors';
import { getActiveTab } from '@/store/modules/table/active-tab';
import { getTableSelectedItem } from '@/store/modules/table/query-getters';
import {
  addTableReportTab,
  clearTableChartHiddenSeries,
  clearTableSelectedItems,
  clearTableTree,
  closeTableTab,
  navigateTableActiveTab,
  patchTableQueryField,
  removeTableReportTabById,
  renameTableReportTab,
  replaceTableColumnsFiltersForCurrentTab,
  setTableChartHiddenSeries,
  setTableChartHiddenSeriesDashboard,
  setTableChartOpen,
  setTableCurrentReport,
  setTableCustomGroups,
  setTableDrilldown,
  setTableDrilldownFilter,
  setTableEntityName,
  setTableExportParams,
  setTableIsHideFilters,
  setTableLastActiveTab,
  setTableLoading,
  setTableNextReportGroup,
  setTableNextReportTreeLevel,
  setTableNoReportBlockVisible,
  setTableNotesOpened,
  setTablePreviewLink,
  setTableProgressBlockVisible,
  setTableSelectedItems,
  setTableTotal,
  setTableTreeLevelsForCurrentTab,
} from '@/store/modules/table/query-actions';
import { createReportModule } from '@/store/modules/report';

export type { CampaignsCacheState } from '@/store/modules/cache/campaigns-cache';
export type { AffiliateNetworksCacheState } from '@/store/modules/cache/affiliate-networks-cache';
export type { IntegrationsCacheState } from '@/store/modules/cache/integrations-cache';

export const useStore = defineStore('store', () => {
  const auth = createAuthModule();
  const mainData = createMainDataModule();
  const billing = createBillingModule();
  const dashboard = createDashboardModule();
  const reportColumns = createReportColumnsModule();
  const cacheModule = createCacheModule();
  const layoutUi = createLayoutUiModule();
  const tableModule = createTableModule();
  const reportModule = createReportModule();

  const resetForLogout = () => {
    auth.reset();
    mainData.reset();
    billing.reset();
    dashboard.reset();
    reportColumns.reset();
    cacheModule.reset();
    layoutUi.reset();
    tableModule.reset();
    reportModule.reset();
  };

  return {
    ...auth.state,
    ...mainData.state,
    ...billing.state,
    ...dashboard.state,
    ...reportColumns.state,
    ...cacheModule.state,
    ...layoutUi.state,
    ...tableModule.state,
    getActiveTab,
    getTableSelectedItem,
    setTableSelectedReport,
    addTableReportTab,
    clearTableChartHiddenSeries,
    clearTableSelectedItems,
    clearTableTree,
    closeTableTab,
    navigateTableActiveTab,
    patchTableQueryField,
    removeTableReportTabById,
    renameTableReportTab,
    replaceTableColumnsFiltersForCurrentTab,
    setTableChartHiddenSeries,
    setTableChartHiddenSeriesDashboard,
    setTableChartOpen,
    setTableCurrentReport,
    setTableCustomGroups,
    setTableDrilldown,
    setTableDrilldownFilter,
    setTableEntityName,
    setTableExportParams,
    setTableIsHideFilters,
    setTableLastActiveTab,
    setTableLoading,
    setTableNextReportGroup,
    setTableNextReportTreeLevel,
    setTableNoReportBlockVisible,
    setTableNotesOpened,
    setTablePreviewLink,
    setTableProgressBlockVisible,
    setTableSelectedItems,
    setTableTotal,
    setTableTreeLevelsForCurrentTab,
    ...reportModule.state,
    resetForLogout,
  };
});
